import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { GuildMemberResponse, GuildRoleResponse } from '@/lib/api';
import { ROLE_DOT_FALLBACK_COLOR } from '../lib/role-colors';

interface GuildMemberRolesDialogProps {
  /** `null` keeps the dialog closed. */
  member: GuildMemberResponse | null;
  roles: GuildRoleResponse[];
  /** Role ids this member currently holds. */
  assignedRoleIds: string[];
  onClose: () => void;
  onAssign: (userId: string, roleId: string) => Promise<unknown>;
  onUnassign: (userId: string, roleId: string) => Promise<unknown>;
}

/** Grants or removes custom roles for a single member. */
export function GuildMemberRolesDialog({
  member,
  roles,
  assignedRoleIds,
  onClose,
  onAssign,
  onUnassign,
}: GuildMemberRolesDialogProps) {
  const [busyRoleId, setBusyRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (roleId: string, checked: boolean) => {
    if (!member) return;
    setBusyRoleId(roleId);
    setError(null);
    try {
      if (checked) await onAssign(member.userId, roleId);
      else await onUnassign(member.userId, roleId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update roles');
    } finally {
      setBusyRoleId(null);
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Roles for {member?.username || member?.userId}</DialogTitle>
          <DialogDescription>
            Custom roles add permissions on top of this member’s base role
            {member ? ` (${member.role})` : ''}.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        {roles.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            This guild has no custom roles yet. Create one in the Roles tab.
          </p>
        ) : (
          <ul className="flex flex-col gap-2 max-h-80 overflow-y-auto">
            {roles.map((role) => {
              const id = `member-role-${role.roleId}`;
              return (
                <li key={role.roleId} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={assignedRoleIds.includes(role.roleId)}
                    disabled={busyRoleId === role.roleId}
                    onCheckedChange={(value) => void toggle(role.roleId, value === true)}
                  />
                  <span
                    aria-hidden="true"
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ background: role.color ?? ROLE_DOT_FALLBACK_COLOR }}
                  />
                  <Label htmlFor={id} className="text-sm font-normal cursor-pointer">
                    {role.name}
                  </Label>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
